import ReactGA from "react-ga4";
import { getRegion } from "@util/domain";

// Google Analytics per region: the AU hub keeps its existing ID;
// the International hub reports to the .co property
const GA_MEASUREMENT_IDS = { au: "G-NF2673GH79", global: "G-ZDZJJT6JZ5" };
const CONSENT_KEY = "fioh-analytics-consent";
export const CONSENT_SETTINGS_EVENT = "fioh:consent-settings";

export type AnalyticsConsent = "granted" | "denied";

let initialised = false;

const measurementId = () => GA_MEASUREMENT_IDS[getRegion()];

// Google Consent Mode v2 (basic), International hub only: Analytics loads after Accept,
// which covers analytics and ad measurement. Ad personalisation (remarketing) is never granted.
const CONSENT_DENIED = {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
};
const CONSENT_GRANTED = {
    ...CONSENT_DENIED,
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
};

// International visitors are asked before Analytics starts (UK and EU cookie rules).
// The AU hub is unchanged.
export const needsAnalyticsConsent = () => getRegion() === "global";

export const getAnalyticsConsent = (): AnalyticsConsent | null => {
    try {
        const value = window.localStorage.getItem(CONSENT_KEY);
        return value === "granted" || value === "denied" ? value : null;
    } catch {
        return null; // Storage blocked: treat as not yet answered
    }
};

// Removes Google Analytics cookies (_ga, _ga_*) from this host and its parent domains
const clearAnalyticsCookies = () => {
    const names = document.cookie.split(";")
        .map((c) => c.split("=")[0].trim())
        .filter((n) => n === "_ga" || n.startsWith("_ga_"));
    const parts = window.location.hostname.split(".");
    const domains = parts.map((_, i) => parts.slice(i).join(".")).filter((d) => d.includes("."));
    names.forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        domains.forEach((domain) => {
            document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
        });
    });
};

export const setAnalyticsConsent = (value: AnalyticsConsent) => {
    try {
        window.localStorage.setItem(CONSENT_KEY, value);
    } catch {
        // Storage blocked: the choice applies to this page view only
    }
    // Google's documented opt-out flag stops any further hits if Analytics
    // was already running on this page
    const flags = window as unknown as Record<string, boolean>;
    flags[`ga-disable-${measurementId()}`] = value === "denied";
    if (initialised) ReactGA.gtag("consent", "update", value === "granted" ? CONSENT_GRANTED : CONSENT_DENIED);
    if (value === "denied") clearAnalyticsCookies();
};

// Reopens the consent bar (footer "Cookie Settings" link) so a visitor can change their choice
export const openConsentSettings = () => {
    window.dispatchEvent(new Event(CONSENT_SETTINGS_EVENT));
};

// Starts Analytics unless on localhost, on the private tribute management page,
// or (International only) before the visitor has accepted
export const initGA = (pathname: string = window.location.pathname) => {
    const { hostname } = window.location;
    const isLocalhost = hostname === "localhost" || hostname.endsWith(".localhost");
    if (initialised || isLocalhost || /^\/tribute\/manage\/?$/i.test(pathname)) return;
    if (needsAnalyticsConsent()) {
        if (getAnalyticsConsent() !== "granted") return;
        // Consent state must reach the dataLayer before initialize sends config
        ReactGA.gtag("consent", "default", CONSENT_DENIED);
        ReactGA.gtag("consent", "update", CONSENT_GRANTED);
    }
    ReactGA.initialize(measurementId());
    initialised = true;
};

// Nothing is sent or queued before Analytics has started
export const trackPageView = (page: string) => {
    if (!initialised) return;
    ReactGA.send({ hitType: "pageview", page });
};

export type AnalyticsIds = { clientId: string; sessionId?: string };

// Reads gtag's value for one field, giving up after a short wait so checkout is never held up
const getGtagField = (field: string): Promise<string | undefined> => new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), 1000);
    ReactGA.gtag("get", measurementId(), field, (value: unknown) => {
        clearTimeout(timer);
        resolve(value === undefined || value === null ? undefined : String(value));
    });
});

// GA client and session IDs for server-side purchase tracking (sent with checkout).
// Undefined when Analytics hasn't started (no consent, blocked or localhost).
export const getAnalyticsIds = async (): Promise<AnalyticsIds | undefined> => {
    if (!initialised) return undefined;
    const [clientId, sessionId] = await Promise.all([getGtagField("client_id"), getGtagField("session_id")]);
    return clientId ? { clientId, sessionId } : undefined;
};

export const trackEvent = (name: string, params?: Record<string, unknown>) => {
    if (!initialised) return;
    ReactGA.event(name, params);
};
